import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, isAdmin } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, ArrowRight, Eye, EyeOff } from "lucide-react";

const LIFECYCLE = ["BUILD", "CLOSE", "OWN", "PROTECT", "SERVICE"];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("login");
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const u = await login(email, password);
      navigate(isAdmin(u) ? "/admin" : "/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const forgot = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-arq-bg text-arq-ink">
      {/* Left: dark editorial panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden border-r border-white/10"
        style={{ background: "radial-gradient(900px 500px at 20% 0%, rgba(62,221,166,0.10), transparent 55%), #0A0F0D" }}>
        <div className="flex items-center gap-2 relative z-10">
          <button onClick={() => navigate("/")} data-testid="login-logo-home" className="transition-opacity hover:opacity-80">
            <img src="/arqitrak-logo.png" alt="ARQITRAK" className="h-7 w-auto" />
          </button>
        </div>
        <div className="max-w-lg relative z-10">
          <p className="text-[11px] uppercase tracking-[0.35em] text-arq-teal mb-6 font-medium">ARQITRAK · Digital Homeownership Platform</p>
          <h1 className="font-display text-6xl leading-[1.02]">
            Everything about your home.<br /><span className="text-arq-teal">In one place. For life.</span>
          </h1>
          <p className="text-muted-foreground leading-relaxed mt-6 text-base max-w-md">
            Track your home's construction, understand everything installed, manage warranties, and request service — the permanent digital record of your Arqitek home.
          </p>
        </div>
        <div className="relative z-10 flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-muted-foreground/70 flex-wrap">
          {LIFECYCLE.map((s, i) => (
            <React.Fragment key={s}>
              <span>{s}</span>{i < LIFECYCLE.length - 1 && <span className="text-arq-teal">→</span>}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm arq-fade-up">
          <div className="lg:hidden flex items-center gap-2 mb-10">
            <button onClick={() => navigate("/")} data-testid="login-logo-home-mobile" className="transition-opacity hover:opacity-80">
              <img src="/arqitrak-logo.png" alt="ARQITRAK" className="h-6 w-auto" />
            </button>
          </div>

          {mode === "login" ? (
            <>
              <h2 className="font-display text-4xl">Welcome back</h2>
              <p className="text-sm text-muted-foreground mt-1 mb-8">Sign in to access your home.</p>
              <form onSubmit={submit} className="space-y-5" data-testid="login-form">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" className="mt-1.5 h-11 bg-arq-surface border-border" />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <button type="button" onClick={() => { setMode("forgot"); setError(""); }} className="text-xs text-arq-teal hover:underline" data-testid="forgot-link">Forgot password?</button>
                  </div>
                  <div className="relative mt-1.5">
                    <Input id="password" type={show ? "text" : "password"} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" className="h-11 bg-arq-surface border-border pr-10" />
                    <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                  </div>
                </div>
                {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
                <Button type="submit" className="w-full h-11 group" disabled={busy} data-testid="login-submit">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></>}
                </Button>
              </form>
            </>
          ) : (
            <>
              <h2 className="font-display text-4xl">Reset password</h2>
              <p className="text-sm text-muted-foreground mt-1 mb-8">Enter your email and we'll send a reset link.</p>
              {sent ? (
                <div className="space-y-4">
                  <div className="p-4 bg-arq-surface border border-border rounded-md text-sm text-muted-foreground">If an account exists for <b className="text-arq-ink">{email}</b>, a reset link has been sent.</div>
                  <Button variant="outline" className="w-full" onClick={() => { setMode("login"); setSent(false); }}>Back to sign in</Button>
                </div>
              ) : (
                <form onSubmit={forgot} className="space-y-5" data-testid="forgot-form">
                  <div>
                    <Label htmlFor="femail">Email</Label>
                    <Input id="femail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 h-11 bg-arq-surface border-border" data-testid="forgot-email" />
                  </div>
                  {error && <p className="text-sm text-destructive">{error}</p>}
                  <Button type="submit" className="w-full h-11" disabled={busy} data-testid="forgot-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Send reset link</Button>
                  <button type="button" onClick={() => setMode("login")} className="text-sm text-arq-teal hover:underline w-full text-center">Back to sign in</button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
